/**
 * Turning outside content — markdown from the chat, HTML or text from the clipboard — into the
 * HTML the manuscript editor itself produces.
 *
 * The manuscript renders with `white-space: pre-wrap`, so the newlines a markdown converter puts
 * between its block tags are not collapsed the way they would be in ordinary HTML: each one
 * becomes an anonymous block box and shows up as a blank line between paragraphs. The editor's own
 * paragraph break is two adjacent block elements with nothing between them, so anything arriving
 * from outside has to be reshaped to match before it lands in the canvas.
 */
import { marked } from 'marked'

/** Elements that begin their own line in the manuscript. */
const BLOCK_LEVEL = new Set([
  'P',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
  'UL',
  'OL',
  'LI',
  'PRE',
  'HR',
  'TABLE',
  'THEAD',
  'TBODY',
  'TR',
  'TD',
  'TH',
  'SECTION',
  'ARTICLE',
  'FIGURE',
  'FIGCAPTION'
])

export const escapeHtmlText = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * Removes whitespace-only text nodes that sit between block elements.
 *
 * Whitespace inside a paragraph is left alone: a parent with no block children holds prose, where
 * the spacing is the author's and a single newline is a line break they can see.
 */
export function stripInterBlockWhitespace(root: ParentNode): void {
  const doomed: ChildNode[] = []

  const visit = (parent: ParentNode): void => {
    const hasBlockChild = Array.from(parent.children).some((child) =>
      BLOCK_LEVEL.has(child.tagName.toUpperCase())
    )
    for (const node of Array.from(parent.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        if (hasBlockChild && (node.textContent ?? '').trim() === '') doomed.push(node)
        continue
      }
      if (node.nodeType === Node.ELEMENT_NODE) visit(node as Element)
    }
  }

  visit(root)
  doomed.forEach((node) => node.remove())
}

/** Runs a fragment of HTML through {@link stripInterBlockWhitespace}. */
export function normalizeManuscriptHtml(html: string): string {
  const document = new DOMParser().parseFromString(html, 'text/html')
  stripInterBlockWhitespace(document.body)
  return document.body.innerHTML
}

const isBlockElement = (node: ChildNode | undefined): boolean =>
  node?.nodeType === Node.ELEMENT_NODE && BLOCK_LEVEL.has((node as Element).tagName.toUpperCase())

/**
 * Trims the newline padding off the front of a text run that follows a block.
 *
 * A merge seam or a published-chapter join leaves markup like `</p>\n\nSome prose`. The block has
 * already ended the line, so under pre-wrap those newlines only add blank ones. Runs that are
 * entirely whitespace are {@link stripInterBlockWhitespace}'s job; this handles the ones that also
 * carry prose.
 *
 * Only leading newlines, and only newlines. A trailing newline before a block costs nothing on
 * screen — the block breaks the line by itself — while removing it does change the app's word
 * count, because htmlToPlainText only inserts a separator for *closing* tags and would fuse the
 * last word of the run into the first word of the next paragraph. Spaces and tabs are left alone
 * everywhere: a leading space may be the writer's, and it is invisible either way.
 */
export function trimBlockBoundaryNewlines(root: ParentNode): void {
  const visit = (parent: ParentNode): void => {
    const children = Array.from(parent.childNodes)
    const isBlockContainer = Array.from(parent.children).some((child) =>
      BLOCK_LEVEL.has(child.tagName.toUpperCase())
    )
    if (isBlockContainer) {
      for (const [index, node] of children.entries()) {
        if (node.nodeType !== Node.TEXT_NODE) continue
        if (index > 0 && !isBlockElement(children[index - 1])) continue
        const text = (node.textContent ?? '').replace(/^[ \t]*\n[ \t\n]*/, '')
        if (text !== node.textContent) node.textContent = text
      }
    }
    for (const child of Array.from(parent.children)) visit(child)
  }
  visit(root)
}

/**
 * Removes the `<br>` separators the old "Insert at End" path left between blocks.
 *
 * That path wrote `<br><br>` before a block that already carried its own paragraph margin. The
 * signature is a break with a block on both sides, so that is the rule — anything else is left
 * alone, and deliberately so: a break inside a paragraph is the writer's line break, and a break
 * that opens a block (`<div><br><div>…`) is what repeated Enter produces at the end of a chapter,
 * an authored blank line rather than a separator.
 */
export function removeStrayBlockBreaks(root: ParentNode): void {
  const doomed: Element[] = []
  const visit = (parent: ParentNode): void => {
    const children = Array.from(parent.childNodes).filter(
      (node) => node.nodeType !== Node.TEXT_NODE || (node.textContent ?? '').trim() !== ''
    )
    const hasBlockBefore = (index: number): boolean =>
      children.slice(0, index).some((node) => isBlockElement(node))
    const hasBlockAfter = (index: number): boolean =>
      children.slice(index + 1).some((node) => isBlockElement(node))

    for (const [index, node] of children.entries()) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue
      const element = node as Element
      if (element.tagName === 'BR' && hasBlockBefore(index) && hasBlockAfter(index)) {
        doomed.push(element)
        continue
      }
      visit(element)
    }
  }
  visit(root)
  doomed.forEach((element) => element.remove())
}

/**
 * The full cleanup for manuscript HTML that was stored before the insert and paste paths were
 * fixed. Used by the one-off backfill, not by the editor.
 *
 * Deliberately never removes an element that holds a line of its own. `<div><br></div>` is exactly
 * what the browser writes when a writer presses Enter on an empty line, and with no scene-break
 * control in the editor it is the only way they can ask for a blank one — so every empty block
 * survives, and what goes is only the whitespace that no one typed.
 */
export function cleanStoredManuscriptHtml(html: string): string {
  const document = new DOMParser().parseFromString(html, 'text/html')
  stripInterBlockWhitespace(document.body)
  trimBlockBoundaryNewlines(document.body)
  removeStrayBlockBreaks(document.body)
  return document.body.innerHTML
}

/** Markdown to manuscript HTML, in the shape the editor produces for the same prose. */
export function markdownToManuscriptHtml(markdown: string): string {
  return normalizeManuscriptHtml(marked.parse(markdown.trim()) as string)
}

/**
 * Plain text to manuscript HTML: a blank line is a paragraph break, a lone newline a hard break.
 *
 * Turning every newline into `<br>` instead would fold the whole paste into one block, which not
 * only doubles the spacing but hides the paragraphs from everything that works block by block —
 * the paragraph minimap, the formatting pass, PDF export and the history diff.
 */
export function plainTextToManuscriptHtml(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter((block) => block !== '')
    .map((block) => `<div>${escapeHtmlText(block).replace(/\n/g, '<br>')}</div>`)
    .join('')
}
